import React, { useEffect, useRef } from "react";
import * as THREE from "three";

import sswMark from "../../../assets/images/brand3d/ssw_mark.png";
import shuklaMedicalMark from "../../../assets/images/brand3d/shukla_medical_mark.png";
import shuklaSurgicalMark from "../../../assets/images/brand3d/shukla_surgical_mark.png";
import combinedMark from "../../../assets/images/brand3d/combined_mark.png";

/**
 * Logo3D — one company's real logo as a full-color flat panel spinning in 3D
 * like a coin (rotates about the vertical axis). The exact logo art is mapped
 * on BOTH faces (back mirrored so it reads correctly), so the logo stays sharp
 * and fully colored all the way around — it just turns through 3D space. No
 * solid slab, so colors are never lost and it never becomes a blob. *
 * `three` is imported statically (CRA's ModuleScopePlugin crashes on a
 * dynamically-imported bare module). Disposed on unmount. Respects
 * prefers-reduced-motion (renders a static, forward-facing frame).
 *
 * Props:
 *   company  "ssw" | "shuklaMedical" | "shuklaSurgical"
 *   size     px square. Default 132.
 *   secPerTurn seconds per 360. Default 7.
 *   spin     Default true.
 */

const MARK_SRC = {
  ssw: sswMark,
  shuklaMedical: shuklaMedicalMark,
  shuklaSurgical: shuklaSurgicalMark,
  combined: combinedMark,
};

const Logo3D = ({
  company = "ssw",
  size = 132,
  secPerTurn = 7,
  spin = true,
  className,
  style,
}) => {
  const mountRef = useRef(null);
  const cleanupRef = useRef(null);
  const spinRef = useRef(spin);
  const speedRef = useRef(secPerTurn);
  spinRef.current = spin;
  speedRef.current = secPerTurn;

  useEffect(() => {
    let disposed = false;
    const mount = mountRef.current;
    if (!mount) return undefined;

    const src = MARK_SRC[company] || MARK_SRC.ssw;
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let renderer;
    try {
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 1000);
      camera.position.set(0, 0, 200);

      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(size, size);
      renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
      mount.appendChild(renderer.domElement);
      renderer.domElement.style.display = "block";

      // Flat art reads best with even, bright light (it's a textured panel).
      scene.add(new THREE.AmbientLight(0xffffff, 1.0));

      const group = new THREE.Group();
      scene.add(group);

      const geos = [];
      const mats = [];

      const loader = new THREE.TextureLoader();
      loader.load(
        src,
        (texture) => {
          if (disposed) {
            texture.dispose();
            return;
          }
          texture.anisotropy = renderer.capabilities.getMaxAnisotropy
            ? renderer.capabilities.getMaxAnisotropy()
            : 1;
          if ("colorSpace" in texture && THREE.SRGBColorSpace) {
            texture.colorSpace = THREE.SRGBColorSpace;
          } else if ("encoding" in texture && THREE.sRGBEncoding) {
            texture.encoding = THREE.sRGBEncoding;
          }

          // Keep the logo's aspect ratio (don't squash non-square logos).
          const img = texture.image;
          const ar = img && img.width && img.height ? img.width / img.height : 1;
          const PLANE = 120;
          const w = ar >= 1 ? PLANE : PLANE * ar;
          const h = ar >= 1 ? PLANE / ar : PLANE;

          const faceGeo = new THREE.PlaneGeometry(w, h);
          geos.push(faceGeo);
          const faceMat = new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            alphaTest: 0.05,
            side: THREE.FrontSide,
            toneMapped: false,
          });
          mats.push(faceMat);

          const front = new THREE.Mesh(faceGeo, faceMat);
          group.add(front);

          // Back face: same art, mirrored, sitting a hair behind so it reads
          // correctly when the coin turns past 90°.
          const back = new THREE.Mesh(faceGeo, faceMat);
          back.rotation.y = Math.PI;
          back.position.z = -0.1;
          group.add(back);
        },
        undefined,
        () => { }
      );

      let raf = 0;
      let last = performance.now();
      const animate = (now) => {
        raf = requestAnimationFrame(animate);
        const dt = (now - last) / 1000;
        last = now;
        if (spinRef.current && !prefersReduced) {
          const sps = (Math.PI * 2) / Math.max(2, speedRef.current);
          group.rotation.y += sps * dt;
        }
        renderer.render(scene, camera);
      };
      raf = requestAnimationFrame(animate);

      cleanupRef.current = () => {
        cancelAnimationFrame(raf);
        geos.forEach((g) => g.dispose());
        mats.forEach((m) => m.dispose());
        renderer.dispose();
        if (renderer.domElement && renderer.domElement.parentNode === mount) {
          mount.removeChild(renderer.domElement);
        }
      };
    } catch (e) {
      if (renderer && renderer.domElement && renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    }

    return () => {
      disposed = true;
      if (cleanupRef.current) cleanupRef.current();
      cleanupRef.current = null;
    };
  }, [company, size]);

  return (
    <span
      ref={mountRef}
      className={className}
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: size,
        height: size,
        lineHeight: 0,
        flexShrink: 0,
        ...style,
      }}
    />
  );
};

export default Logo3D;
